import { useState } from "react";
import { motion } from "framer-motion";
import { X, Send, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestAccessModal({ onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", guests: "", budget: "", event_interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const inputCls = "w-full bg-[#1A1A1A] border border-[#262626] px-5 py-3.5 text-white font-['Outfit'] font-light text-sm placeholder:text-white/25 focus:outline-none focus:border-[#C5A059]/50 transition-colors";

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry`, { name: form.name, email: form.email, phone: form.phone, company: form.company, enquiry_type: "tailored_experience",
        message: [form.message, form.guests && `Guests: ${form.guests}`, form.budget && `Budget: ${form.budget}`, form.event_interest && `Interest: ${form.event_interest}`].filter(Boolean).join("\n") });
      toast.success(t("request_success") || "Request sent!");
      onClose();
    } catch { toast.error("Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="request-access-modal" className="relative bg-[#141414] border border-[#262626] max-w-lg w-full z-10 max-h-[95vh] overflow-y-auto">
        <button onClick={onClose} data-testid="close-request-modal" className="absolute top-4 right-4 text-white/40 hover:text-white z-10"><X size={20} /></button>
        <div className="p-8">
          <div className="flex items-center gap-3 mb-2"><Sparkles size={18} className="text-[#C5A059]" /><span className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-['Outfit'] font-semibold">{t("request_tag")}</span></div>
          <h3 className="font-['Cormorant_Garamond'] text-3xl font-light text-white mb-2">{t("request_title")}</h3>
          <p className="text-white/45 font-['Outfit'] font-light text-sm mb-8 leading-relaxed">{t("request_desc")}</p>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input name="name" value={form.name} onChange={handleChange} placeholder={t("full_name")} required data-testid="request-name" className={inputCls} />
              <input name="email" type="email" value={form.email} onChange={handleChange} placeholder={t("email")} required data-testid="request-email" className={inputCls} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input name="phone" value={form.phone} onChange={handleChange} placeholder={t("phone")} data-testid="request-phone" className={inputCls} />
              <input name="company" value={form.company} onChange={handleChange} placeholder={t("enquiry_company")} data-testid="request-company" className={inputCls} />
            </div>
            <div className="pt-2"><p className="text-white/40 text-xs font-['Outfit'] uppercase tracking-wider mb-3">{t("request_tag")}</p></div>
            <input name="event_interest" value={form.event_interest} onChange={handleChange} placeholder={t("request_event_interest")} data-testid="request-event-interest" className={inputCls} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input name="guests" value={form.guests} onChange={handleChange} placeholder={t("request_guests")} data-testid="request-guests" className={inputCls} />
              <input name="budget" value={form.budget} onChange={handleChange} placeholder={t("request_budget")} data-testid="request-budget" className={inputCls} />
            </div>
            <textarea name="message" value={form.message} onChange={handleChange} placeholder={t("request_message")} required rows={4} data-testid="request-message" className={`${inputCls} resize-none`} />
            <button type="submit" disabled={loading} data-testid="request-submit-btn" className="btn-primary w-full py-4 text-xs tracking-[0.2em] uppercase font-['Outfit'] font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? t("request_sending") : t("request_send")} {!loading && <Send size={14} />}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
